/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input} from '@angular/core';
import template from './report-template-gridster-item.component.html';
import {OgTranslateService} from '../common/filters/og-translate.service';

export interface ReportTemplateGridsterResource {
  model: any;
  widgets: Record<string, any>;
  toolbarButtons: string[][];
  dateFormats: Record<string, any[]>;
  isAxAdvertiser: boolean;
  removeComponent: (component: any) => void;
  timeChange: (component: any) => void;
  markDirty: () => void;
}

@Component({
  standalone: false,
  selector: 'adn-report-template-gridster-item',
  template,
  host: {
    class: 'gridster-item-host',
    style: 'display:flex;flex-direction:column;flex:1;min-height:0;height:100%;width:100%;overflow:hidden;'
  }
})
export class ReportTemplateGridsterItemComponent {
  @Input() component: any;
  @Input() resource: ReportTemplateGridsterResource;

  private cachedWidgetFields: any;
  private cachedFieldEntries: Array<{key: string; value: any}> = [];

  constructor(private readonly ogTranslate: OgTranslateService) {
  }

  get widgetDef(): any {
    return this.resource?.widgets?.[this.component?.reportComponent?.id];
  }

  get hasTextParam(): boolean {
    return this.component?.parameters?.text === '' || !!this.component?.parameters?.text;
  }

  get hasHeadingParam(): boolean {
    return !!this.component?.parameters?.heading;
  }

  get fieldEntries(): Array<{key: string; value: any}> {
    const fields = this.widgetDef?.parameters?.fields;
    if (!fields) {
      return [];
    }
    if (fields !== this.cachedWidgetFields) {
      this.cachedWidgetFields = fields;
      this.cachedFieldEntries = Object.keys(fields).map((key) => ({key, value: fields[key]}));
    }
    return this.cachedFieldEntries;
  }

  hasWidgetParam(param: string): boolean {
    return this.widgetDef?.parameters?.[param] !== undefined;
  }

  fieldLabel(param: string): string {
    const key = 'reportTemplate.option.' + (this.component?.translationOverrides?.[param] || param);
    return this.ogTranslate.transform(key);
  }

  trackByFieldKey(_index: number, entry: {key: string; value: any}): string {
    return entry.key;
  }

  fieldChecked(param: string): boolean {
    return !!this.component?.parameters?.fields?.[param];
  }

  setFieldChecked(param: string, checked: boolean): void {
    this.component.parameters = this.component.parameters || {};
    this.component.parameters.fields = this.component.parameters.fields || {};
    this.component.parameters.fields[param] = checked;
    this.markDirty();
  }

  dateFormatOptions(timeFormat?: string): any[] {
    return this.resource?.dateFormats?.[timeFormat || 'NONE'] || [];
  }

  dateFormatLabel(option: any): string {
    if (!option?.format?.length) {
      return option?.example || '';
    }
    return option.example + ' (' + option.format + ')';
  }

  remove(): void {
    this.resource?.removeComponent?.(this.component);
  }

  markDirty(): void {
    this.resource?.markDirty?.();
  }

  onTimeChange(): void {
    this.resource?.timeChange?.(this.component);
    this.markDirty();
  }
}
